import { Navigate, createBrowserRouter, type RouteObject } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { AnonymousOnly, RequireAuth, RequirePermission, RequireWorkspace } from "@/auth/guards";
import { AppShell } from "@/components/AppShell";
import { ComingSoon } from "@/components/ComingSoon";
import { RouteError } from "@/components/ErrorBoundary";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { ActivityLog } from "@/pages/ActivityLog";
import { Analytics } from "@/pages/Analytics";
import { Aftersales } from "@/pages/aftersales/Aftersales";
import { CaseDetail } from "@/pages/aftersales/CaseDetail";
import { Claims } from "@/pages/aftersales/Claims";
import { Catalog } from "@/pages/catalog/Catalog";
import { Listings } from "@/pages/catalog/Listings";
import { Pricing } from "@/pages/catalog/Pricing";
import { StockSync } from "@/pages/inventory/StockSync";
import { ProductEditor } from "@/pages/products/ProductEditor";
import { Products } from "@/pages/products/Products";
import { Duplicates } from "@/pages/catalog/Duplicates";
import { SkuDetail } from "@/pages/catalog/SkuDetail";
import { Channels } from "@/pages/channels/Channels";
import { Dashboard } from "@/pages/Dashboard";
import { NotFound } from "@/pages/ErrorPages";
import { Inventory } from "@/pages/inventory/Inventory";
import { HoldRules } from "@/pages/orders/HoldRules";
import { Couriers } from "@/pages/orders/Couriers";
import { Promotions } from "@/pages/orders/Promotions";
import { OrderDetail } from "@/pages/orders/OrderDetail";
import { Orders } from "@/pages/orders/Orders";
import { AcceptInvite } from "@/pages/auth/AcceptInvite";
import { Login } from "@/pages/auth/Login";
import { ForgotPassword, ResetPassword } from "@/pages/auth/PasswordReset";
import { Register } from "@/pages/auth/Register";
import { SupportSession } from "@/pages/auth/SupportSession";
import { SubscriptionPage } from "@/pages/settings/Subscription";
import { StoragePage } from "@/pages/settings/Storage";
import { Verify } from "@/pages/auth/Verify";
import { Onboarding } from "@/pages/onboarding/Onboarding";
import { CompanySettings } from "@/pages/settings/Company";
import { CountDetail } from "@/pages/warehouse/CountDetail";
import { Counts } from "@/pages/warehouse/Counts";
import { Inbound } from "@/pages/warehouse/Inbound";
import { InboundDetail } from "@/pages/warehouse/InboundDetail";
import { Locations } from "@/pages/warehouse/Locations";
import { Containers } from "@/pages/warehouse/Containers";
import { Pallets } from "@/pages/warehouse/Pallets";
import { Barcodes } from "@/pages/tools/Barcodes";
import { BinStock } from "@/pages/warehouse/BinStock";
import { StaffPerformance } from "@/pages/warehouse/StaffPerformance";
import { TaskDetail } from "@/pages/warehouse/TaskDetail";
import { Tasks } from "@/pages/warehouse/Tasks";
import { Variance } from "@/pages/warehouse/Variance";
import { Serials } from "@/pages/warehouse/Serials";
import { Damaged } from "@/pages/warehouse/Damaged";
import { Cancelled } from "@/pages/warehouse/Cancelled";
import { BillDetail } from "@/pages/finance/BillDetail";
import { Cash } from "@/pages/finance/Cash";
import { Receivables } from "@/pages/finance/Receivables";
import { Bills } from "@/pages/finance/Bills";
import { Costs } from "@/pages/finance/Costs";
import { Margins } from "@/pages/finance/Margins";
import { PurchaseOrderDetail } from "@/pages/procurement/PurchaseOrderDetail";
import { VendorReturnDetail } from "@/pages/procurement/VendorReturnDetail";
import { VendorReturns } from "@/pages/procurement/VendorReturns";
import { Credits } from "@/pages/finance/Credits";
import { Reports } from "@/pages/finance/Reports";
import { Books } from "@/pages/finance/Books";
import { ManualOrderNew } from "@/pages/orders/ManualOrderNew";
import { PurchaseOrders } from "@/pages/procurement/PurchaseOrders";
import { SupplierDetail } from "@/pages/procurement/SupplierDetail";
import { Suppliers } from "@/pages/procurement/Suppliers";
import { Pools } from "@/pages/warehouse/Pools";
import { WaveDetail, Waves } from "@/pages/warehouse/Waves";
import { ManifestDetail, Manifests } from "@/pages/warehouse/Manifests";
import { Packagings } from "@/pages/warehouse/Packagings";
import { Packing } from "@/pages/warehouse/Packing";
import { Returns } from "@/pages/warehouse/Returns";
import { TransferDetail, Transfers } from "@/pages/warehouse/Transfers";
import { LabelSettings } from "@/pages/settings/Labels";
import { UserSettings } from "@/pages/settings/Users";
import { WarehouseSettings } from "@/pages/settings/Warehouses";

/**
 * The marketing site stays on zerpid.com; this app (go.zerpid.com) opens straight
 * on sign-in, or on the app for a returning session.
 */
function Root() {
  const { status } = useAuth();
  if (status === "loading") return <FullScreenLoader />;
  return <Navigate to={status === "authenticated" ? "/app" : "/login"} replace />;
}

// Routes are English and never translated (App Flow §13 #7); labels follow the user's language.
/**
 * Pages inside the app shell (/app/*). The work tabs render them per tab
 * (components/tabs/WorkTabs.tsx), which is what keeps a hidden tab alive.
 * Paths are relative to /app.
 */
export const appPages: RouteObject[] = [
  { index: true, element: <RequirePermission any={["dashboard.view"]}><Dashboard /></RequirePermission> },
  { path: "orders", element: <RequirePermission any={["orders.view"]}><Orders /></RequirePermission> },
  { path: "promotions", element: <RequirePermission any={["orders.view"]}><Promotions /></RequirePermission> },
  { path: "couriers", element: <RequirePermission any={["orders.view"]}><Couriers /></RequirePermission> },
  { path: "orders/hold-rules", element: <RequirePermission any={["orders.view"]}><HoldRules /></RequirePermission> },
  { path: "orders/new", element: <RequirePermission any={["orders.manage"]}><ManualOrderNew /></RequirePermission> },
  { path: "orders/:orderID", element: <RequirePermission any={["orders.view"]}><OrderDetail /></RequirePermission> },
  { path: "aftersales", element: <RequirePermission any={["orders.view", "warehouse.operate"]}><Aftersales /></RequirePermission> },
  { path: "aftersales/claims", element: <RequirePermission any={["orders.view", "warehouse.operate"]}><Claims /></RequirePermission> },
  { path: "aftersales/:caseID", element: <RequirePermission any={["orders.view", "warehouse.operate"]}><CaseDetail /></RequirePermission> },
  { path: "products", element: <RequirePermission any={["catalog.view"]}><Products /></RequirePermission> },
  { path: "products/:productID", element: <RequirePermission any={["catalog.view"]}><ProductEditor /></RequirePermission> },
  { path: "stock-sync", element: <RequirePermission any={["inventory.view"]}><StockSync /></RequirePermission> },
  { path: "catalog", element: <RequirePermission any={["catalog.view"]}><Catalog /></RequirePermission> },
  { path: "listings", element: <RequirePermission any={["catalog.view"]}><Listings /></RequirePermission> },
  { path: "prices", element: <RequirePermission any={["catalog.view"]}><Pricing /></RequirePermission> },
  { path: "catalog/duplicates", element: <RequirePermission any={["catalog.view"]}><Duplicates /></RequirePermission> },
  { path: "catalog/:skuID", element: <RequirePermission any={["catalog.view"]}><SkuDetail /></RequirePermission> },
  { path: "inventory", element: <RequirePermission any={["inventory.view"]}><Inventory /></RequirePermission> },
  { path: "warehouse/pallets", element: <RequirePermission any={["warehouse.operate"]}><Pallets /></RequirePermission> },
  { path: "tools/barcodes", element: <RequirePermission any={["warehouse.operate", "catalog.view"]}><Barcodes /></RequirePermission> },
  { path: "warehouse/containers", element: <RequirePermission any={["warehouse.operate"]}><Containers /></RequirePermission> },
  { path: "warehouse/performance", element: <RequirePermission any={["warehouse.operate"]}><StaffPerformance /></RequirePermission> },
  { path: "warehouse/stock", element: <RequirePermission any={["inventory.view", "warehouse.operate"]}><BinStock /></RequirePermission> },
  { path: "warehouse/locations", element: <RequirePermission any={["warehouse.operate", "settings.warehouses"]}><Locations /></RequirePermission> },
  { path: "warehouse/inbound", element: <RequirePermission any={["warehouse.operate"]}><Inbound /></RequirePermission> },
  { path: "warehouse/inbound/:receiptID", element: <RequirePermission any={["warehouse.operate"]}><InboundDetail /></RequirePermission> },
  { path: "warehouse/putaway", element: <RequirePermission any={["warehouse.operate"]}><Tasks kind="putaway" /></RequirePermission> },
  { path: "warehouse/tasks/:taskID", element: <RequirePermission any={["warehouse.operate"]}><TaskDetail /></RequirePermission> },
  { path: "warehouse/pools", element: <RequirePermission any={["warehouse.operate"]}><Pools /></RequirePermission> },
  { path: "warehouse/waves", element: <RequirePermission any={["warehouse.operate"]}><Waves /></RequirePermission> },
  { path: "warehouse/waves/:waveID", element: <RequirePermission any={["warehouse.operate"]}><WaveDetail /></RequirePermission> },
  { path: "warehouse/picking", element: <RequirePermission any={["warehouse.operate"]}><Tasks kind="picking" /></RequirePermission> },
  { path: "warehouse/packing", element: <RequirePermission any={["warehouse.operate"]}><Packing /></RequirePermission> },
  { path: "warehouse/packing/packagings", element: <RequirePermission any={["warehouse.operate"]}><Packagings /></RequirePermission> },
  { path: "warehouse/outbound", element: <RequirePermission any={["warehouse.operate"]}><Manifests /></RequirePermission> },
  { path: "warehouse/outbound/:manifestID", element: <RequirePermission any={["warehouse.operate"]}><ManifestDetail /></RequirePermission> },
  { path: "warehouse/returns", element: <RequirePermission any={["warehouse.operate"]}><Returns /></RequirePermission> },
  { path: "warehouse/cancelled", element: <RequirePermission any={["warehouse.operate"]}><Cancelled /></RequirePermission> },
  { path: "warehouse/transfers", element: <RequirePermission any={["warehouse.operate"]}><Transfers /></RequirePermission> },
  { path: "warehouse/transfers/:transferID", element: <RequirePermission any={["warehouse.operate"]}><TransferDetail /></RequirePermission> },
  { path: "warehouse/counts", element: <RequirePermission any={["warehouse.operate"]}><Counts /></RequirePermission> },
  { path: "warehouse/counts/:countID", element: <RequirePermission any={["warehouse.operate"]}><CountDetail /></RequirePermission> },
  { path: "warehouse/variance", element: <RequirePermission any={["inventory.view", "warehouse.manage"]}><Variance /></RequirePermission> },
  { path: "warehouse/serials", element: <RequirePermission any={["warehouse.operate", "inventory.view"]}><Serials /></RequirePermission> },
  { path: "warehouse/damaged", element: <RequirePermission any={["warehouse.operate", "inventory.view"]}><Damaged /></RequirePermission> },
  { path: "procurement/orders", element: <RequirePermission any={["procurement.view"]}><PurchaseOrders /></RequirePermission> },
  { path: "procurement/orders/:poID", element: <RequirePermission any={["procurement.view"]}><PurchaseOrderDetail /></RequirePermission> },
  { path: "procurement/returns", element: <RequirePermission any={["procurement.view"]}><VendorReturns /></RequirePermission> },
  { path: "procurement/returns/:returnID", element: <RequirePermission any={["procurement.view"]}><VendorReturnDetail /></RequirePermission> },
  { path: "procurement/suppliers", element: <RequirePermission any={["procurement.view"]}><Suppliers /></RequirePermission> },
  { path: "procurement/suppliers/:supplierID", element: <RequirePermission any={["procurement.view"]}><SupplierDetail /></RequirePermission> },
  { path: "warehouse/*", element: <RequirePermission any={["warehouse.operate"]}><ComingSoon feature="nav.warehouse" /></RequirePermission> },
  { path: "channels/*", element: <RequirePermission any={["channels.view"]}><Channels /></RequirePermission> },
  { path: "logs", element: <RequirePermission any={["logs.all", "logs.warehouse", "logs.orders"]}><ActivityLog /></RequirePermission> },
  { path: "settings", element: <Navigate to="/app/settings/company" replace /> },
  { path: "settings/company", element: <RequirePermission any={["settings.company"]}><CompanySettings /></RequirePermission> },
  { path: "settings/warehouses", element: <RequirePermission any={["settings.warehouses"]}><WarehouseSettings /></RequirePermission> },
  { path: "settings/labels", element: <RequirePermission any={["settings.warehouses"]}><LabelSettings /></RequirePermission> },
  { path: "settings/users", element: <RequirePermission any={["settings.users"]}><UserSettings /></RequirePermission> },
  { path: "billing", element: <RequirePermission any={["billing.manage"]}><SubscriptionPage /></RequirePermission> },
  { path: "storage", element: <RequirePermission any={["billing.manage", "settings.warehouses"]}><StoragePage /></RequirePermission> },
  { path: "finance", element: <Navigate to="/app/finance/bills" replace /> },
  { path: "finance/receivables", element: <RequirePermission any={["finance.view"]}><Receivables /></RequirePermission> },
  { path: "finance/cash", element: <RequirePermission any={["finance.view"]}><Cash /></RequirePermission> },
  { path: "finance/costs", element: <RequirePermission any={["finance.view"]}><Costs /></RequirePermission> },
  { path: "finance/margins", element: <RequirePermission any={["finance.view"]}><Margins /></RequirePermission> },
  { path: "finance/books", element: <RequirePermission any={["finance.view"]}><Books /></RequirePermission> },
  { path: "finance/reports", element: <RequirePermission any={["finance.view"]}><Reports /></RequirePermission> },
  { path: "finance/credits", element: <RequirePermission any={["finance.view"]}><Credits /></RequirePermission> },
  { path: "finance/bills", element: <RequirePermission any={["finance.view"]}><Bills /></RequirePermission> },
  { path: "finance/bills/:billID", element: <RequirePermission any={["finance.view"]}><BillDetail /></RequirePermission> },
  { path: "analytics", element: <RequirePermission any={["orders.view"]}><Analytics /></RequirePermission> },
  { path: "*", element: <NotFound /> },
];

export const router = createBrowserRouter([
  {
    // One error screen for the whole app instead of React Router's developer page.
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <Root /> },
      {
        element: <AnonymousOnly />,
        children: [
          { path: "/login", element: <Login /> },
          { path: "/register", element: <Register /> },
          { path: "/forgot-password", element: <ForgotPassword /> },
        ],
      },
      // Reachable signed in or out: the link comes from an email.
      { path: "/verify", element: <Verify /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "/invite/:token", element: <AcceptInvite /> },
      // Console Zerpid opens a read-only support session here (Plan §6.16).
      { path: "/support", element: <SupportSession /> },
      {
        element: <RequireAuth />,
        children: [
          { path: "/onboarding", element: <Onboarding /> },
          {
            element: <RequireWorkspace />,
            children: [
              // The work tabs render the pages themselves (useRoutes per tab),
              // so the shell owns everything under /app.
              { path: "/app/*", element: <AppShell routes={appPages} /> },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
